<template>
    <div class="calendar">
        <table class="calendar__table">
            <thead>
                <tr>
                    <th colspan="7">
                        <div class="calendar__table-months-panel">
                            <button
                                class="calendar__table-month-btn calendar__table-month-btn--rotated btn btn-link center-content"
                                @click="showPrevMonth"
                                :disabled="!canGoPrev"
                                type="button"
                                role="button"
                                slot="arrow-left"
                                aria-label="Vormonat"
                            >
                                <IconSvg filepath="arrow-right.svg"></IconSvg>
                            </button>
                            <span class="calendar__table-month-year text-truncate" slot="month" aria-live="polite"
                                >{{ monthName }} {{ selectedMonthDate.getFullYear() }}</span
                            >
                            <button
                                class="calendar__table-month-btn btn btn-link next-month center-content"
                                @click="showNextMonth"
                                :disabled="!canGoNext"
                                type="button"
                                role="button"
                                slot="arrow-right"
                                aria-label="nächsten Monat"
                            >
                                <IconSvg filepath="arrow-right.svg"></IconSvg>
                            </button>
                        </div>
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr class="calendar__table-weekdays">
                    <td v-for="(day, index) in weekDays" :key="'weekday' + index">
                        {{ day }}
                    </td>
                </tr>
                <tr v-for="(week, weekIndex) in maxWeeks" :key="'week' + weekIndex">
                    <td
                        v-for="(day, dayIndex) in 7"
                        :key="'day' + weekIndex + dayIndex"
                        class="calendar__table-day-cell"
                    >
                        <div v-if="tableWithMonth[dayIndex + 7 * weekIndex]" class="calendar__table-date-wrapper">
                            <a
                                class="calendar__table-date"
                                href="#"
                                @click.prevent.stop="
                                    onDateSelect(tableWithMonth[dayIndex + 7 * weekIndex].dateFormatted)
                                "
                                :tabindex="!tableWithMonth[dayIndex + 7 * weekIndex].selectable && -1"
                                :class="{
                                    'calendar__table-date--selectable':
                                        tableWithMonth[dayIndex + 7 * weekIndex].selectable,
                                    'calendar__table-date--active': tableWithMonth[dayIndex + 7 * weekIndex].active,
                                    'calendar__table-date--readonly': readonly,
                                    ['calendar__table-date--price-' +
                                    tableWithMonth[dayIndex + 7 * weekIndex].priceGroup]:
                                        !!tableWithMonth[dayIndex + 7 * weekIndex].priceGroup
                                }"
                                :aria-disabled="!tableWithMonth[dayIndex + 7 * weekIndex].selectable || readonly"
                            >
                                {{ tableWithMonth[dayIndex + 7 * weekIndex].date }}
                            </a>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td colspan="7" aria-hidden="true">
                        <slot></slot>
                    </td>
                </tr>
            </tbody>
        </table>

        <div v-show="gradationByPrice && monthHasSelectableDates && priceGroupsForMonth" class="calendar__table-legend">
            <!-- v-show here as v-if causes hydration issue -->
            <span v-show="priceGroupsForMonth && priceGroupsForMonth.min != null" class="calendar__table-legend-item">
                <i class="calendar__table-legend-color calendar__table-legend-color--low" />
                <span>{{ $t('Price.prefixDefault').toLowerCase() }}</span>
                <span v-if="priceGroupsForMonth && priceGroupsForMonth.min != null">{{
                    formatPrice(priceGroupsForMonth.min)
                }}</span>
            </span>
            <!-- v-show here as v-if causes hydration issue -->
            <span v-show="monthHasMediumPrices" class="calendar__table-legend-item">
                <i class="calendar__table-legend-color calendar__table-legend-color--medium" />
                <span>{{ $t('Price.prefixDefault').toLowerCase() }}</span>
                <span v-if="priceGroupsForMonth && priceGroupsForMonth.lowerAverage != null">{{
                    formatPrice(priceGroupsForMonth.lowerAverage)
                }}</span>
            </span>
            <!-- v-show here as v-if causes hydration issue -->
            <span v-show="monthHasHighPrices" class="calendar__table-legend-item">
                <i class="calendar__table-legend-color calendar__table-legend-color--high" />
                <span>{{ $t('Price.prefixDefault').toLowerCase() }}</span>
                <span v-if="priceGroupsForMonth && priceGroupsForMonth.higherAverage != null">{{
                    formatPrice(priceGroupsForMonth.higherAverage)
                }}</span>
            </span>
        </div>
    </div>
</template>

<script>
import formatPrice from '@/filters/formatPrice';
import { read } from '@popperjs/core';

const parsers = {
    de(date) {
        if (!date || date.length !== 10) {
            return null;
        }
        const [day, month, year] = date.split('.');
        return new Date(+year, +month - 1, +day);
    },
    'de-DE'(date) {
        return this.de(date);
    },
    en(date) {
        return new Date(date);
    },
    'en-EN'(date) {
        return this.en(date);
    }
};

const priceGroups = {
    low: 'low',
    medium: 'medium',
    high: 'high'
};

export default {
    name: 'Calendar',
    data() {
        return {
            maxWeeks: 6,
            selectedMonthDate: new Date()
        };
    },
    props: {
        /**
         * @typedef {Object} Variant
         * @property {string} arrivalDate
         * @property {Object[]} variants
         */
        /**
         * @type {string[] | Variant[]}
         */
        availableDates: {
            type: Array,
            required: true
        },
        /**
         * Intl locale
         */
        locale: {
            type: String,
            default: 'de-DE'
        },
        selected: {
            type: String
        },
        gradationByPrice: Boolean,
        readonly: Boolean
    },
    computed: {
        /**
         * Looks at the structure of the available dates incoming from the backend
         * @returns {this is *[]}
         */
        datesOnlyProvided() {
            return this.availableDates.every(d => typeof d === 'string');
        },
        availableDatesComputed() {
            return this.datesOnlyProvided ? this.availableDates : this.availableDates.map(date => date.arrivalDate);
        },
        tableWithMonth() {
            return Array.from({ length: 7 * this.maxWeeks }).map((x, cellIndex) => {
                //Populate array with objects when dates of month = cellIndex with offset only
                if (cellIndex >= this.firstMonthCellIndex && cellIndex < this.daysInMonth + this.firstMonthCellIndex) {
                    const dayNumber = cellIndex - this.firstMonthCellIndex + 1;
                    const dateFormatted = this.formatDate(
                        new Date(this.selectedMonthDate.getFullYear(), this.selectedMonthDate.getMonth(), dayNumber)
                    ).replace(/\u200E/g, ''); //replace is needed for IE

                    const correspondentEntry = this.datesOnlyProvided
                        ? this.availableDatesComputed.find(e => e === dateFormatted)
                        : this.lowestPriceToDatesInMonth.find(e => e.arrivalDate === dateFormatted);
                    const priceGroup =
                        this.gradationByPrice && correspondentEntry && correspondentEntry.lowestPrice
                            ? this.getPriceGroup(correspondentEntry.lowestPrice)
                            : false;

                    return {
                        date: dayNumber,
                        dateFormatted,
                        selectable: !!correspondentEntry,
                        active: this.selected === dateFormatted,
                        priceGroup
                    };
                }
                return null;
            });
        },
        monthHasSelectableDates() {
            return this.tableWithMonth.some(d => d && d.selectable);
        },
        monthHasHighPrices() {
            return this.tableWithMonth.some(e => e && e.priceGroup === priceGroups.high);
        },
        monthHasMediumPrices() {
            return this.tableWithMonth.some(e => e && e.priceGroup === priceGroups.medium);
        },
        lowestPriceToDatesInMonth() {
            return this.datesOnlyProvided
                ? []
                : this.availableDates
                      .map(item => {
                          if (Array.isArray(item.variants) && item.arrivalDate) {
                              const arrivalDateFormatted = parsers[this.locale](item.arrivalDate);
                              const inSelectedMonth =
                                  arrivalDateFormatted.getMonth() === this.selectedMonthDate.getMonth();

                              if (inSelectedMonth) {
                                  const lowestPrice = item.variants.reduce((final, variant) => {
                                      const prices = this.$getSafe(variant, 'price.regularPriceValues') || {};
                                      return Math.min(final, prices.price);
                                  }, Infinity);

                                  return { lowestPrice, arrivalDate: item.arrivalDate };
                              }
                          }
                      })
                      .filter(Boolean);
        },
        /**
         * @typedef { higherAverage: number, min: number, max: number, lowerAverage: number } MonthGroup
         */
        /**
         * @returns {MonthGroup}
         */
        priceGroupsForMonth() {
            if (this.datesOnlyProvided) {
                return null;
            }

            const lowestPricesInMonth = this.lowestPriceToDatesInMonth.map(i => i.lowestPrice);

            const min = Math.min(...lowestPricesInMonth);
            // case when no price information is available - multivoucher shop for instance
            // min monthly price is always rendered, there should avoid if it is NaN
            if (isNaN(min)) {
                return null;
            }

            const max = Math.max(...lowestPricesInMonth);

            const range = max - min;
            const lowerAverage = range * 0.33 + min;
            const higherAverage = range * 0.66 + min;
            const average = (min + max) / 2;

            return { min, lowerAverage, average, higherAverage, max };
        },
        availableDatesSorted() {
            return [...this.availableDatesComputed]
                .map(dateString => {
                    return parsers[this.locale](dateString);
                })
                .sort((a, b) => (a < b ? -1 : 1));
        },
        minAvailableDate() {
            return this.availableDatesSorted[0];
        },
        maxAvailableDate() {
            return this.availableDatesSorted[this.availableDatesSorted.length - 1];
        },
        canGoPrev() {
            return new Date(this.selectedMonthDate).setDate(0) >= this.minAvailableDate; //Date(this.selectedMonthDate).setDate(0) = last day of prev month, if bigger than minimal, minimal date is earlier, there can go prev
        },
        canGoNext() {
            return this.getMonth(1) <= this.maxAvailableDate; //if the first of next month is less than maximal date, maximal date is later, there can go next
        },
        monthName() {
            return new Intl.DateTimeFormat(this.locale, { month: 'long' }).format(this.selectedMonthDate);
        },
        weekDays() {
            const formatter = new Intl.DateTimeFormat(this.locale, {
                weekday: 'short'
            });
            const date = new Date();
            const day = date.getDay() || 7; //Sunday == 0, converting to 7, making monday == 1 day
            if (day !== 1) {
                date.setHours(-24 * (day - 1));
            }

            return Array.from({ length: 7 }).map((x, dayOffset) => {
                const relativeDate = new Date(date).setDate(date.getDate() + dayOffset);
                return formatter.format(relativeDate);
            });
        },
        firstMonthCellIndex() {
            const firstSundayBased = new Date(
                this.selectedMonthDate.getFullYear(),
                this.selectedMonthDate.getMonth(),
                1
            ).getDay();
            return firstSundayBased ? firstSundayBased - 1 : 6; //to make zero-based and Sunday as day number 7
        },
        daysInMonth() {
            return 32 - new Date(this.selectedMonthDate.getFullYear(), this.selectedMonthDate.getMonth(), 32).getDate();
        }
    },
    methods: {
        read() {
            return read;
        },
        getMonth(dir = 1) {
            return new Date(this.selectedMonthDate.getFullYear(), this.selectedMonthDate.getMonth() + +dir);
        },
        showNextMonth() {
            this.selectedMonthDate = this.getMonth(1);
        },
        showPrevMonth() {
            this.selectedMonthDate = this.getMonth(-1);
        },
        onDateSelect(formattedDate) {
            this.$emit('update:selected', formattedDate);
        },
        formatDate(date) {
            return new Intl.DateTimeFormat(this.locale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }).format(date);
        },
        /**
         * @param {number} price
         * @returns {"low" | "medium" | "high"}
         */
        getPriceGroup(price) {
            const { min, lowerAverage, average, higherAverage, max } = this.priceGroupsForMonth;
            if (price <= lowerAverage) {
                return priceGroups.low;
            } else if (price <= higherAverage) {
                return priceGroups.medium;
            } else return priceGroups.high;
        },
        formatPrice(euroCents) {
            const roundedСents = Math.floor(euroCents / 100) * 100; // better rounding for more attractable lower formatted prices
            return formatPrice(roundedСents, true);
        }
    },
    created() {
        if (this.selected) {
            // sometimes selected is null, sometimes empty string. TS to the rescue
            this.selectedMonthDate = parsers[this.locale](this.selected);
        } else {
            // This opens calendar with the first month with available dates
            this.selectedMonthDate = new Date(this.minAvailableDate);
        }
        if (!this.selectedMonthDate || this.selectedMonthDate.toString() === 'Invalid Date') {
            this.selectedMonthDate = new Date();
        }
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/mixins';
@use '@/styles/theming' as *;
@use 'sass:map';

.calendar {
    //$selectable-bg: hsla(91, 76%, 87%);
    $selectable-bg: hsla(90, 76%, 56%, 0.3);
    $selectable-bg-medium: darken(opacify($selectable-bg, 0.2), 20%);
    $selectable-bg-high: darken(opacify($selectable-bg, 0.5), 20%);

    &__table {
        border: 1px solid var(--border-color);
    }

    &__table-months-panel {
        border-bottom: 1px solid var(--border-color);
    }

    &__table-day-cell {
        border: 1px solid var(--border-color);
    }

    &__table-months-panel,
    &__table-day-cell {
        background-color: var(--white);
    }

    &__table-date {
        background-color: var(--border-color);
        color: var(--muted-accessible, color-mix(in srgb, var(--border-color), black 20%));
    }

    &__table {
        width: calc(100% + 2px);
        text-align: center;
        table-layout: fixed;
        margin: 0 -1px;
    }

    @include mixins.mobile-only {
        //for tablets
        max-width: 400px;
        margin: 0 auto;
    }

    &__table-weekdays,
    &__table-months-panel {
        line-height: 40px;
    }

    &__table-months-panel {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    &__table-month-btn {
        outline: none;
        font-size: 30px;
        padding: 0 10px;
        line-height: 1em;
        display: flex;
        min-height: 48px; // mobile usability

        &--rotated {
            transform: rotate(180deg);
        }
    }

    &__table-month-year {
        font-size: 19px;
        font-weight: normal;
    }

    &__table-date-wrapper {
        width: 100%;
        padding-top: 100%;
        position: relative;
    }

    &__table-date {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        align-items: center;
        display: flex;
        justify-content: center;
        pointer-events: none;
        text-decoration: none;

        &--selectable {
            color: var(--body-color);
            background-color: $selectable-bg;
            pointer-events: all;

            &--focus {
                background-color: map.get(variables.$inputs, 'filled-color');
            }
        }

        &--price-medium {
            background-color: $selectable-bg-medium;
        }

        &--price-high {
            background-color: $selectable-bg-high;
        }

        &--active,
        &--selectable:hover,
        &--selectable:focus {
            background-color: var(--primary);
            color: var(--primary-text-color, var(--white));
        }

        &--readonly {
            pointer-events: none;
        }
    }

    &__table-legend {
        display: flex;
        justify-content: center;
        gap: 20px;
        padding-top: 15px;
        padding-bottom: 15px;
    }

    &__table-legend-item {
        display: inline-flex;
        align-items: center;
        gap: 3px;
    }

    &__table-legend-color {
        height: 20px;
        width: 20px;

        &--low {
            background-color: $selectable-bg;
        }
        &--medium {
            background-color: $selectable-bg-medium;
        }
        &--high {
            background-color: $selectable-bg-high;
        }
    }

    tbody > tr:last-child > td:empty {
        display: none;
    }
}
</style>
